import { z } from 'zod'

import { TermsAcceptanceSchema } from './legal-agreement'
import { OnboardingStateSchema } from './onboarding'
import { MemberRole } from './organization-invite'

// Account type: a Personal account is an Organization row with a slimmer module
// set. Const object (not a TS enum) to match the FeatureFlagKey convention.
export const AccountType = {
  ORGANIZATION: 'ORGANIZATION',
  PERSONAL: 'PERSONAL',
} as const

export type AccountType = (typeof AccountType)[keyof typeof AccountType]

// Organization schema with validation
export const OrganizationSchema = z.object({
  id: z.string().uuid().optional(),
  name: z.string().min(1, 'Organization name is required'),
  slug: z.string().optional(),
  accountType: z.nativeEnum(AccountType).optional(),
  settings: z.record(z.unknown()).optional(),
  onboardingState: OnboardingStateSchema.nullable().optional(),
  logo: z.string().nullable().optional(),

  // Organization information fields
  type: z.string().nullable().optional(),
  description: z
    .string()
    .max(500, 'Description must be 500 characters or less')
    .nullable()
    .optional(),
  zipcode: z.string().nullable().optional(),
  teamSize: z.string().nullable().optional(),
  defaultMaxProjectCount: z.number().int().nullable().optional(),

  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
})

// Organization role type
export type OrganizationRole = MemberRole

// Organization type
export type Organization = z.infer<typeof OrganizationSchema>

// DTO for creating an organization
export const CreateOrganizationSchema = OrganizationSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  // Legal agreement acceptance (required for org creation)
  legalAgreements: TermsAcceptanceSchema.optional(),
})

export type CreateOrganizationDTO = z.infer<typeof CreateOrganizationSchema>

// DTO for updating an organization
export const UpdateOrganizationSchema = OrganizationSchema.partial().omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})

export type UpdateOrganizationDTO = z.infer<typeof UpdateOrganizationSchema>

export const RESERVED_ORGANIZATION_NAMES = [
  'Adobe',
  'Airbnb',
  'Alphabet',
  'Amazon',
  'Amazon Web Services',
  'Apple',
  'Atlassian',
  'Bank of America',
  'Cisco',
  'Citigroup',
  'Cloudflare',
  'Coinbase',
  'Datadog',
  'Deloitte',
  'Discord',
  'Dropbox',
  'Ernst & Young',
  'Facebook',
  'Figma',
  'GitHub',
  'Google',
  'HubSpot',
  'IBM',
  'Instagram',
  'Intuit',
  'JPMorgan Chase',
  'LinkedIn',
  'Mastercard',
  'Meta',
  'Meta Platforms',
  'Microsoft',
  'Netflix',
  'Nvidia',
  'OpenAI',
  'Oracle',
  'PayPal',
  'Salesforce',
  'Samsung',
  'Shopify',
  'Slack',
  'Snap',
  'Snowflake',
  'Stripe',
  'TikTok',
  'Toyota',
  'Twilio',
  'Uber',
  'Visa',
  'Walmart',
  'X',
  'X Corp',
  'YouTube',
  'Zoom',
] as const

const RESERVED_ORGANIZATION_NAME_SUFFIXES = [
  'co',
  'company',
  'corp',
  'corporation',
  'gmbh',
  'inc',
  'incorporated',
  'llc',
  'ltd',
  'limited',
] as const

function normalizeOrganizationNameForReservation(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function normalizeOrganizationSlugForReservation(value: string): string {
  return normalizeOrganizationNameForReservation(value).replace(/\s+/g, '-')
}

function compactOrganizationReservationKey(value: string): string {
  return value.replace(/[\s-]+/g, '')
}

function stripOrganizationLegalSuffixes(value: string): string {
  const suffixes = new Set<string>(RESERVED_ORGANIZATION_NAME_SUFFIXES)
  const parts = value.split(' ').filter(Boolean)

  while (parts.length > 1 && suffixes.has(parts[parts.length - 1])) {
    parts.pop()
  }

  return parts.join(' ')
}

const RESERVED_ORGANIZATION_NAME_KEYS = new Set(
  RESERVED_ORGANIZATION_NAMES.flatMap(name => {
    const normalized = normalizeOrganizationNameForReservation(name)
    const stripped = stripOrganizationLegalSuffixes(normalized)
    return [
      normalized,
      stripped,
      compactOrganizationReservationKey(normalized),
      compactOrganizationReservationKey(stripped),
    ]
  }),
)

const RESERVED_ORGANIZATION_SLUG_KEYS = new Set(
  RESERVED_ORGANIZATION_NAMES.flatMap(name => {
    const normalized = normalizeOrganizationNameForReservation(name)
    const stripped = stripOrganizationLegalSuffixes(normalized)
    const normalizedSlug = normalizeOrganizationSlugForReservation(normalized)
    const strippedSlug = normalizeOrganizationSlugForReservation(stripped)
    return [
      normalizedSlug,
      strippedSlug,
      compactOrganizationReservationKey(normalizedSlug),
      compactOrganizationReservationKey(strippedSlug),
    ]
  }),
)

export function isReservedOrganizationName(name: string): boolean {
  const normalized = normalizeOrganizationNameForReservation(name)
  const stripped = stripOrganizationLegalSuffixes(normalized)
  const compact = compactOrganizationReservationKey(stripped)
  return (
    RESERVED_ORGANIZATION_NAME_KEYS.has(normalized) ||
    RESERVED_ORGANIZATION_NAME_KEYS.has(stripped) ||
    RESERVED_ORGANIZATION_NAME_KEYS.has(compact)
  )
}

export function isReservedOrganizationSlug(slug: string): boolean {
  const normalized = normalizeOrganizationSlugForReservation(slug)
  // Public connection setup occupies this root route independently of organizations.
  if (normalized === 'connect') return true
  const stripped = normalizeOrganizationSlugForReservation(
    stripOrganizationLegalSuffixes(
      normalizeOrganizationNameForReservation(slug),
    ),
  )
  const compact = compactOrganizationReservationKey(normalized)
  const strippedCompact = compactOrganizationReservationKey(stripped)
  return (
    RESERVED_ORGANIZATION_SLUG_KEYS.has(normalized) ||
    RESERVED_ORGANIZATION_SLUG_KEYS.has(stripped) ||
    RESERVED_ORGANIZATION_SLUG_KEYS.has(compact) ||
    RESERVED_ORGANIZATION_SLUG_KEYS.has(strippedCompact)
  )
}

// Organization type options
export const OrganizationTypes = [
  'Agency',
  'Education',
  'Energy',
  'Finance',
  'Government',
  'Healthcare',
  'Hospitality',
  'Legal',
  'Manufacturing',
  'Media',
  'Nonprofit',
  'Real Estate',
  'Retail',
  'Technology',
  'Transportation',
  'Other',
] as const

export type OrganizationType = (typeof OrganizationTypes)[number]

// Team size options
export const TeamSizes = [
  'Solo',
  '2-5',
  '6-10',
  '11-25',
  '26-50',
  '51-100',
  '101-500',
  '500+',
] as const

export type TeamSize = (typeof TeamSizes)[number]
